// src/components/coordinateGrid/CoordinateGridView.tsx
/**
 * CoordinateGridView Component
 * 
 * Main canvas component for the 2D coordinate grid view mode.
 * Integrates all grid, axes, and interaction layers.
 * 
 * Features:
 * - Configurable background grid
 * - X and Y axes with ticks and labels
 * - Pan and zoom support (integrated with existing viewport system)
 * - Quadrant visibility control
 * - Real-time coordinate display
 * 
 * Phase 1: Core infrastructure
 */

import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import { pan, zoomAt, centerOrigin } from '../../store/slices/viewportSlice';
import { selectActiveTool } from '../../store/slices/toolModeSlice';
import { GridRenderer } from './GridRenderer';
import { AxesRenderer } from './AxesRenderer';
import { CoordinateDisplay } from './CoordinateDisplay';
import { EquationRenderer } from './EquationRenderer';
import { EquationInputWidget } from './EquationInputWidget';
import { selectEquationWidget, hideEquationWidget, addEquation, updateEquation } from '../../store/slices/equationsSlice';
import { PointRenderer } from './PointRenderer';
import { ShapeRenderer } from './ShapeRenderer';
import { addPoint, selectAllPoints, selectPoint } from '../../store/slices/plotPointsSlice';
import { startDrawingShape, addDrawingVertex, completeShape, cancelDrawing } from '../../store/slices/gridShapesSlice';
import { selectDrawingShape } from '../../store/slices/gridShapesSlice';
import { calculateDistance, snapToGrid } from '../../lib/coordinateGrid/shapeCalculations';
import { startMeasurement, updateMeasurement, completeMeasurement, cancelMeasurement } from '../../store/slices/uiSlice';
import { CoordinateTable } from './CoordinateTable';
import { selectAllTables } from '../../store/slices/coordinateTablesSlice';
import { TransformRenderer } from './TransformRenderer';
import { Measurement2DRenderer } from './Measurement2DRenderer';
import { VectorRenderer } from './VectorRenderer';
import { InequalityRenderer } from './InequalityRenderer';
import { startDrawingVector, setVectorStart, completeVector, cancelDrawing as cancelVectorDrawing, selectDrawingVector } from '../../store/slices/vectorsSlice';

interface CoordinateGridViewProps {
  /** SVG width in pixels */
  width: number;
  /** SVG height in pixels */
  height: number;
  /** Additional CSS class name */
  className?: string;
  /** ID for the SVG element */
  id?: string;
  /** Ref for export functionality */
  exportRef?: React.RefObject<HTMLDivElement>;
}

// Extended zoom range for educational use
const MIN_SCALE = 0.1;
const MAX_SCALE = 10000;
const ZOOM_FACTOR = 1.1;

export const CoordinateGridView: React.FC<CoordinateGridViewProps> = ({
  width,
  height,
  className = '',
  id = 'coordinate-grid',
  exportRef,
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // State from Redux
  const viewport = useSelector((state: RootState) => state.viewport);
  const coordinateGrid = useSelector((state: RootState) => state.coordinateGrid);
  const preferences = useSelector((state: RootState) => state.preferences);
  const activeTool = useSelector(selectActiveTool);
  const equationWidget = useSelector(selectEquationWidget);
  const drawingShape = useSelector(selectDrawingShape);
  const drawingVector = useSelector(selectDrawingVector);
  const allPoints = useSelector(selectAllPoints);
  const coordinateTables = useSelector(selectAllTables);
  const measurement = useSelector((state: RootState) => state.ui.measurement);

  // Local state for interactions
  const [isPanning, setIsPanning] = useState(false);
  const [lastPointerPosition, setLastPointerPosition] = useState<{ x: number; y: number } | null>(null);

  // Dark mode
  const isDark = preferences.theme === 'dark';

  // Use equal scaling if enabled
  const scaleX = viewport.scale;
  const scaleY = coordinateGrid.equalScaling ? viewport.scale : (viewport.scale);
  const offsetX = viewport.offsetX;
  const offsetY = viewport.offsetY;

  // Merge container ref with export ref
  const mergedRef = useCallback(
    (el: HTMLDivElement | null) => {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      if (exportRef && 'current' in exportRef) {
        (exportRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    },
    [exportRef]
  );

  // =========================================================================
  // Pan & Zoom Handlers
  // =========================================================================

  /**
   * Convert screen coordinates to math coordinates
   */
  const screenToMath = useCallback(
    (screenX: number, screenY: number): { x: number; y: number } => {
      return {
        x: (screenX - offsetX) / scaleX,
        y: (offsetY - screenY) / scaleY, // Y is inverted
      };
    },
    [scaleX, scaleY, offsetX, offsetY]
  );

  /**
   * Handle pointer down for panning, point placement, and shape drawing
   */
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;

      // Handle point tool
      if (activeTool === 'point') {
        e.preventDefault();
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          const screenX = e.clientX - rect.left;
          const screenY = e.clientY - rect.top;
          let mathCoords = screenToMath(screenX, screenY);
          
          // Apply grid snapping to half units (0.5) unless Alt key is held
          const usePrecisePosition = e.altKey;
          if (!usePrecisePosition) {
            mathCoords = snapToGrid(mathCoords, 0.5);
          }
          
          // Check if Shift key is held to force adding a new point
          const forceAdd = e.shiftKey;
          
          if (!forceAdd) {
            // Check if click is near an existing point (within 0.5 units in math coordinates)
            // Convert threshold to math coordinates (approximately 10 pixels)
            const clickThreshold = 10 / Math.min(scaleX, scaleY);
            
            let nearestPoint = null;
            let nearestDistance = Infinity;
            
            for (const point of allPoints) {
              const distance = calculateDistance(mathCoords, { x: point.x, y: point.y });
              if (distance < clickThreshold && distance < nearestDistance) {
                nearestDistance = distance;
                nearestPoint = point;
              }
            }
            
            // If we found a nearby point, select it instead of adding a new one
            if (nearestPoint) {
              dispatch(selectPoint({ 
                id: nearestPoint.id, 
                ctrlKey: e.ctrlKey || e.metaKey 
              }));
              return;
            }
          }
          
          // No nearby point found, or Shift key held - add a new point
          dispatch(addPoint({ x: mathCoords.x, y: mathCoords.y }));
        }
        return;
      }

      // Handle shape tool
      if (activeTool === 'shape') {
        e.preventDefault();
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          const screenX = e.clientX - rect.left;
          const screenY = e.clientY - rect.top;
          let mathCoords = screenToMath(screenX, screenY);

          // Apply grid snapping to half units (0.5) unless Alt key is held
          const usePrecisePosition = e.altKey;
          if (!usePrecisePosition) {
            mathCoords = snapToGrid(mathCoords, 0.5);
          }

          // Start drawing if not already drawing
          if (!drawingShape || !drawingShape.isDrawing) {
            // Default to polygon for now (can be made configurable)
            dispatch(startDrawingShape('polygon'));
          }

          // Add vertex
          dispatch(addDrawingVertex(mathCoords));
        }
        return;
      }

      // Handle vector tool (Phase 7)
      if (activeTool === 'vector') {
        e.preventDefault();
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          const screenX = e.clientX - rect.left;
          const screenY = e.clientY - rect.top;
          let mathCoords = screenToMath(screenX, screenY);
          
          // Apply grid snapping to half units (0.5) unless Alt key is held
          const usePrecisePosition = e.altKey;
          if (!usePrecisePosition) {
            mathCoords = snapToGrid(mathCoords, 0.5);
          }
          
          if (!drawingVector || !drawingVector.isDrawing) {
            // Start drawing a new vector
            dispatch(startDrawingVector());
            dispatch(setVectorStart(mathCoords));
          } else if (drawingVector.start) {
            // Complete the vector
            dispatch(completeVector({ end: mathCoords }));
          }
        }
        return;
      }

      // Handle measurement tool (Phase 6: 2D support)
      if (activeTool === 'measurement') {
        e.preventDefault();
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          const screenX = e.clientX - rect.left;
          const screenY = e.clientY - rect.top;
          const mathCoords = screenToMath(screenX, screenY);
          
          if (!measurement.isActive) {
            // Start new measurement - store both screen and math coordinates
            dispatch(startMeasurement({
              x: screenX,
              y: screenY, // Store y for 2D
              value: mathCoords.x, // For 1D compatibility
            }));
          } else {
            // Complete measurement
            const startPoint = measurement.startPoint;
            if (startPoint) {
              // Get start math coordinates
              const startMath = screenToMath(startPoint.x, startPoint.y || screenY);
              
              dispatch(completeMeasurement({
                point1: startMath,
                point2: mathCoords,
                showCalculation: true,
                showComponents: true,
                displayMode: 'both',
              }));
            }
          }
        }
        return;
      }

      // Handle pan tool
      if (activeTool === 'pan') {
        e.preventDefault();
        setIsPanning(true);
        setLastPointerPosition({ x: e.clientX, y: e.clientY });
        
        // Capture pointer for smooth dragging
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      }
    },
    [activeTool, dispatch, screenToMath, drawingShape, allPoints, scaleX, scaleY]
  );

  /**
   * Handle pointer move for panning and measurement
   */
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (isPanning && lastPointerPosition) {
        const deltaX = e.clientX - lastPointerPosition.x;
        const deltaY = e.clientY - lastPointerPosition.y;

        dispatch(pan({ deltaX, deltaY }));
        setLastPointerPosition({ x: e.clientX, y: e.clientY });
      }
      
      // Phase 6: Update measurement during drag
      if (activeTool === 'measurement' && measurement.isActive && measurement.startPoint) {
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          const screenX = e.clientX - rect.left;
          const screenY = e.clientY - rect.top;
          const mathCoords = screenToMath(screenX, screenY);
          
          dispatch(updateMeasurement({
            x: screenX,
            value: mathCoords.x, // For 1D compatibility
          }));
        }
      }
    },
    [isPanning, lastPointerPosition, dispatch, activeTool, measurement, screenToMath]
  );

  /**
   * Handle pointer up to end panning
   */
  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      setIsPanning(false);
      setLastPointerPosition(null);
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    },
    []
  );

  /**
   * Handle wheel for zooming
   */
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      // Require Ctrl/Cmd for zoom
      if (!e.ctrlKey && !e.metaKey) {
        // Without modifier, scroll to pan
        dispatch(pan({ deltaX: -e.deltaX, deltaY: -e.deltaY }));
        return;
      }

      e.preventDefault();

      // Get mouse position relative to SVG
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate new scale
      const direction = e.deltaY < 0 ? 1 : -1;
      const newScale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, viewport.scale * Math.pow(ZOOM_FACTOR, direction))
      );

      // Zoom at mouse position
      dispatch(zoomAt({ scale: newScale, anchorX: mouseX, anchorY: mouseY }));
    },
    [dispatch, viewport.scale]
  );

  /**
   * Center the origin in the viewport
   */
  const handleCenterOrigin = useCallback(() => {
    dispatch(centerOrigin({ width, height }));
  }, [dispatch, width, height]);

  // =========================================================================
  // Keyboard Shortcuts
  // =========================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're focused on an input
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      // Center origin with 'O' or '0'
      if (e.key === 'o' || e.key === 'O' || e.key === '0') {
        handleCenterOrigin();
        e.preventDefault();
      }

      // Reset zoom with 'R'
      if (e.key === 'r' || e.key === 'R') {
        dispatch(zoomAt({ scale: 50, anchorX: width / 2, anchorY: height / 2 }));
        handleCenterOrigin();
        e.preventDefault();
      }

      // Zoom in with '+' or '='
      if (e.key === '+' || e.key === '=') {
        const newScale = Math.min(MAX_SCALE, viewport.scale * ZOOM_FACTOR);
        dispatch(zoomAt({ scale: newScale, anchorX: width / 2, anchorY: height / 2 }));
        e.preventDefault();
      }

      // Zoom out with '-'
      if (e.key === '-') {
        const newScale = Math.max(MIN_SCALE, viewport.scale / ZOOM_FACTOR);
        dispatch(zoomAt({ scale: newScale, anchorX: width / 2, anchorY: height / 2 }));
        e.preventDefault();
      }

      // Pan with arrow keys
      const panAmount = e.shiftKey ? 100 : 20;
      if (e.key === 'ArrowLeft') {
        dispatch(pan({ deltaX: panAmount, deltaY: 0 }));
        e.preventDefault();
      }
      if (e.key === 'ArrowRight') {
        dispatch(pan({ deltaX: -panAmount, deltaY: 0 }));
        e.preventDefault();
      }
      if (e.key === 'ArrowUp') {
        dispatch(pan({ deltaX: 0, deltaY: panAmount }));
        e.preventDefault();
      }
      if (e.key === 'ArrowDown') {
        dispatch(pan({ deltaX: 0, deltaY: -panAmount }));
        e.preventDefault();
      }

      // Complete shape drawing with Enter
      if (e.key === 'Enter' && drawingShape?.isDrawing) {
        dispatch(completeShape());
        e.preventDefault();
      }

      // Cancel shape drawing with Escape
      if (e.key === 'Escape' && drawingShape?.isDrawing) {
        dispatch(cancelDrawing());
        e.preventDefault();
      }

      // Cancel vector drawing with Escape
      if (e.key === 'Escape' && drawingVector?.isDrawing) {
        dispatch(cancelVectorDrawing());
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, viewport.scale, width, height, handleCenterOrigin, drawingShape, drawingVector]);

  // =========================================================================
  // Cursor Style
  // =========================================================================

  const cursorStyle = useMemo(() => {
    if (activeTool === 'pan') {
      return isPanning ? 'grabbing' : 'grab';
    }
    return 'crosshair';
  }, [activeTool, isPanning]);

  // =========================================================================
  // Background Color
  // =========================================================================

  const backgroundColor = isDark ? '#111827' : '#FFFFFF';

  // =========================================================================
  // Equation Widget Handlers
  // =========================================================================

  const handleEquationSubmit = useCallback((expression: string) => {
    // The widget handles dispatching add/updateEquation internally
    // We just need to hide it
    dispatch(hideEquationWidget());
  }, [dispatch]);

  const handleEquationCancel = useCallback(() => {
    dispatch(hideEquationWidget());
  }, [dispatch]);

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div
      ref={mergedRef}
      className={`coordinate-grid-container ${className}`}
      style={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor,
        userSelect: 'none',
      }}
      data-coordinate-grid-container
    >
      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        id={id}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="coordinate-grid-svg"
        style={{
          display: 'block',
          cursor: cursorStyle,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
      >
        {/* Background */}
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={backgroundColor}
          className="grid-background"
        />

        {/* Grid Layer */}
        {coordinateGrid.showGrid && (
          <GridRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
        )}

        {/* Axes Layer */}
        <AxesRenderer
          width={width}
          height={height}
          scaleX={scaleX}
          scaleY={scaleY}
          offsetX={offsetX}
          offsetY={offsetY}
          isDark={isDark}
        />

        {/* Plot Layer - Phase 2: Equations, Phase 3: Points and Shapes, Phase 5: Transformations */}
        <g className="plot-layer">
          {/* Render inequalities first (so they appear behind everything) */}
          <InequalityRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
          {/* Render shapes (so they appear behind points) */}
          <ShapeRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
          {/* Render equations */}
          <EquationRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
          {/* Render transformations */}
          <TransformRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
          {/* Render vectors */}
          <VectorRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
          {/* Render 2D measurements (Phase 6) */}
          <Measurement2DRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
          {/* Render points last (so they appear on top) */}
          <PointRenderer
            width={width}
            height={height}
            scaleX={scaleX}
            scaleY={scaleY}
            offsetX={offsetX}
            offsetY={offsetY}
            isDark={isDark}
          />
        </g>
      </svg>

      {/* Coordinate Display Overlay */}
      <CoordinateDisplay
        width={width}
        height={height}
        scaleX={scaleX}
        scaleY={scaleY}
        offsetX={offsetX}
        offsetY={offsetY}
        containerRef={containerRef}
        isDark={isDark}
      />

      {/* Equation Input Widget */}
      {equationWidget && (
        <EquationInputWidget
          isVisible={equationWidget.isVisible}
          position={equationWidget.position}
          mode={equationWidget.mode}
          initialExpression={equationWidget.initialExpression}
          equationId={equationWidget.equationId || undefined}
          onSubmit={handleEquationSubmit}
          onCancel={handleEquationCancel}
        />
      )}

      {/* Coordinate Tables - Phase 4 */}
      {coordinateTables.map((table) => (
        <CoordinateTable
          key={table.id}
          table={table}
          containerRef={containerRef}
          isDark={isDark}
        />
      ))}

      {/* Pan hint (shown briefly on first load) */}
      {activeTool === 'pan' && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '6px 12px',
            borderRadius: 6,
            backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
            fontSize: 12,
            color: isDark ? '#9CA3AF' : '#6B7280',
            pointerEvents: 'none',
            opacity: 0.8,
            zIndex: 50,
          }}
        >
          Drag to pan • Ctrl+Scroll to zoom • Press O to center
        </div>
      )}

      {/* Styles */}
      <style>{`
        .coordinate-grid-container {
          touch-action: none;
        }
        
        .coordinate-grid-svg {
          touch-action: none;
        }
        
        .grid-line {
          pointer-events: none;
        }
        
        .grid-dot {
          pointer-events: none;
        }
        
        .axis-line {
          pointer-events: none;
        }
        
        .tick-label {
          pointer-events: none;
          user-select: none;
        }
        
        .axis-label {
          pointer-events: none;
          user-select: none;
        }
      `}</style>
    </div>
  );
};

export default CoordinateGridView;
